import React from 'react';
import { calcZeroDollarMediaBudget } from '@/features/line-items/line-item-form/utils/line-item-form-utils';
import { isNil } from 'lodash';
import { formatCurrency } from '@/components/tables/table-utils';
import {
  CalculatedField,
  isAboveZero
} from '@/features/line-items/line-item-form/ui/calculated-fields-components';
import {
  EstimatedUnitsTooltip,
  MediaBudgetTooltip,
  PlannedMarginTooltip,
  PricesTooltip
} from '@/features/line-items/line-item-form/zero-dollar/zero-dollar-line-item-tooltips';

type CalculationInput = {
  estimated_units: number | undefined;
  unit_price_type_id: number | undefined;
  planned_unit_cost: number | undefined;
  agency_markup_rate: number | undefined;
  referral_commission_rate: number | undefined;
};

type Props = {
  inputs: CalculationInput;
};

export function ZeroDollarLineItemCalculatedFields({ inputs }: Props) {
  const { estimated_units, planned_unit_cost, agency_markup_rate, referral_commission_rate } =
    inputs;

  if (isNil(estimated_units) && isNil(planned_unit_cost)) return null;

  const is_agency_markup = isAboveZero(agency_markup_rate);
  const is_referral_commission = isAboveZero(referral_commission_rate);

  return (
    <div className="flex flex-col gap-2">
      <CalculatedField
        label={`Prices${is_referral_commission ? ' & Referral Fee' : ''}`}
        detail="$0.00"
        rightText="Zero Dollar Line Item"
        tooltip={
          <PricesTooltip
            isReferralCommission={is_referral_commission}
            isAgencyMarkup={is_agency_markup}
          />
        }
      />
      <CalculatedField
        label="Planned Margin"
        detail="N/A"
        rightText="Zero Dollar Line Item"
        tooltip={<PlannedMarginTooltip />}
      />
    </div>
  );
}

export function ZeroDollarLineItemMediaTeamCalculatedFields({ inputs }: Props) {
  const { estimated_units, planned_unit_cost, unit_price_type_id } = inputs;
  const mediaBudget = calcZeroDollarMediaBudget(estimated_units, planned_unit_cost);
  if (isNil(estimated_units) || isNil(mediaBudget)) return null;

  return (
    <div className="flex flex-col gap-2">
      <CalculatedField
        label="Planned Margin"
        detail="N/A"
        rightText="Zero Dollar Line Item"
        tooltip={<PlannedMarginTooltip />}
      />
      <CalculatedField
        label="Estimated Units"
        detail={estimated_units.toLocaleString()}
        rightText="Entered above"
        tooltip={<EstimatedUnitsTooltip />}
      />
      <CalculatedField
        label="Media Budget"
        detail={formatCurrency(mediaBudget)}
        tooltip={
          <MediaBudgetTooltip
            estimated_units={estimated_units}
            unit_cost={planned_unit_cost}
            unit_price_type_id={unit_price_type_id}
          />
        }
      />
    </div>
  );
}
